import { useQuery } from '@apollo/client';
import { GET_ENVIRONMENT_CONFIG } from '@shared/graphql/queries/environmentConfig';
import { useAnalysisType } from '../contexts/AnalysisTypeContext';

interface GlobalPaywallResult {
  shouldEnforcePaywall: boolean;
  shouldEnforcePaywallForDate: (date?: Date | string) => boolean;
  isPaywallTestUser: boolean;
  globalPaywallEnabled: boolean;
  loading: boolean;
  error: any;
}

/**
 * Hook to determine if paywall should be enforced for the current user
 * Combines individual user paywall_test_user flag with global paywall setting
 * Now includes date-based paywall logic for historical vs current/future races
 */
export const useGlobalPaywall = (): GlobalPaywallResult => {
  const { isPaywallTestUser } = useAnalysisType();
  
  // Fetch the environment config which includes the global paywall flag
  const { data: envData, loading, error } = useQuery(GET_ENVIRONMENT_CONFIG, {
    fetchPolicy: 'cache-first', // Use cache to avoid unnecessary network requests
    errorPolicy: 'ignore' // Don't fail if environment config is unavailable
  });

  const globalPaywallEnabled = envData?.environmentConfig?.paywallEnabled ?? false;
  
  // Paywall should be enforced if:
  // 1. Individual user has paywall_test_user flag, OR
  // 2. Global paywall is enabled for all users
  const shouldEnforcePaywall = isPaywallTestUser || globalPaywallEnabled;

  // Function to check if paywall should be enforced for a specific date
  const shouldEnforcePaywallForDate = (date?: Date | string): boolean => {
    if (!date) {
      // If no date provided, use current paywall logic
      console.log('🔍 [PAYWALL DEBUG] No date provided, using shouldEnforcePaywall:', shouldEnforcePaywall);
      return shouldEnforcePaywall;
    }
    
    // Convert to Date object if string and normalize to Australia/Brisbane timezone
    const meetingDate = typeof date === 'string' ? new Date(date) : date;
    
    // Get current date in Australia/Brisbane timezone for accurate comparison
    const now = new Date();
    const brisbaneToday = new Date(now.toLocaleString("en-US", {timeZone: "Australia/Brisbane"}));
    
    // Set both dates to start of day for accurate comparison
    const meetingDateNormalized = new Date(meetingDate);
    meetingDateNormalized.setHours(0, 0, 0, 0);
    brisbaneToday.setHours(0, 0, 0, 0);
    
    console.log('🔍 [PAYWALL DEBUG] Date comparison:', {
      originalDate: date,
      meetingDate: meetingDate.toISOString(),
      meetingDateNormalized: meetingDateNormalized.toISOString(),
      now: now.toISOString(),
      brisbaneToday: brisbaneToday.toISOString(),
      isHistorical: meetingDateNormalized < brisbaneToday,
      shouldEnforcePaywall,
      isPaywallTestUser,
      globalPaywallEnabled
    });
    
    // If meeting is before today (Brisbane time), never enforce paywall (historical data)
    if (meetingDateNormalized < brisbaneToday) {
      console.log('🔍 [PAYWALL DEBUG] Meeting is historical, never enforce paywall');
      return false;
    }
    
    // For today and future meetings, use normal paywall logic
    // The actual subscription validation is handled by the backend via accessibleTracks
    console.log('🔍 [PAYWALL DEBUG] Meeting is current/future, shouldEnforcePaywall:', shouldEnforcePaywall);
    return shouldEnforcePaywall;
  };

  return {
    shouldEnforcePaywall,
    shouldEnforcePaywallForDate,
    isPaywallTestUser, // Individual user flag
    globalPaywallEnabled, // Global site flag
    loading,
    error
  };
};